
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Invoice } from '@/types/models';
import { parseCurrencyToCents } from '@/lib/format';

const formSchema = z.object({
  family_id: z.string().uuid(),
  total: z.string().min(1, "Amount is required"),
  due_date: z.date({
    required_error: "Due date is required",
  }),
  status: z.enum(['draft', 'unpaid', 'paid', 'overdue', 'partial', 'void'] as const),
});

export type InvoiceFormValues = z.infer<typeof formSchema>;

interface UseInvoiceFormProps {
  invoice?: Invoice;
  initialFamilyId?: string;
}

export const useInvoiceForm = ({ invoice, initialFamilyId }: UseInvoiceFormProps) => {
  const form = useForm<InvoiceFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      family_id: invoice?.family_id || initialFamilyId || '',
      total: invoice ? ((invoice.total_cents / 100).toFixed(2)) : '',
      due_date: invoice ? new Date(invoice.due_date) : new Date(),
      status: invoice?.status || 'draft',
    },
  });

  // Properly format currency input without adding unexpected digits
  const formatCurrencyInput = (value: string) => {
    // Remove all non-numeric characters except decimal point
    let numericValue = value.replace(/[^0-9.]/g, '');
    
    // Ensure only one decimal point
    const parts = numericValue.split('.');
    if (parts.length > 2) {
      numericValue = parts[0] + '.' + parts.slice(1).join('');
    }
    
    // Limit to 2 decimal places
    if (parts.length > 1 && parts[1].length > 2) {
      numericValue = parts[0] + '.' + parts[1].substring(0, 2);
    }
    
    // Return empty string for empty input
    if (!numericValue) return '';
    
    // Format the numeric value
    return numericValue;
  };

  const handleCurrencyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;
    
    // Format the raw value
    const formattedValue = formatCurrencyInput(rawValue);
    
    // Save the formatted value to the form
    form.setValue('total', formattedValue);
  };

  return {
    form,
    handleCurrencyChange,
    formatCurrencyInput,
  };
};
